/** Formatting for the club detail Usage card (quota bars). */

const UNITS = ["B", "KB", "MB", "GB", "TB"] as const;

export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return "0 B";
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < UNITS.length - 1) {
    value /= 1024;
    unit += 1;
  }
  const rounded = value >= 100 || unit === 0 ? Math.round(value) : Math.round(value * 10) / 10;
  return `${rounded} ${UNITS[unit]}`;
}

/** Used/limit as a 0–100+ percentage; null limit (unlimited) or a
 * zero/negative limit yields null (no bar). */
export function usagePercent(used: number, limit: number | null): number | null {
  if (limit === null || limit <= 0) return null;
  return Math.round((used / limit) * 100);
}

/** Bar tone by fullness: amber at ≥90%, red at ≥100%. */
export function usageTone(percent: number | null): "ok" | "warn" | "over" {
  if (percent === null) return "ok";
  if (percent >= 100) return "over";
  if (percent >= 90) return "warn";
  return "ok";
}

const GB = 1024 ** 3;

/** Parsed quota-field value: blank means unlimited (null); a malformed or
 * negative entry is `invalid` so the editor can reject it rather than
 * silently clearing the quota to unlimited. */
export type ParsedQuota =
  | { ok: true; value: number | null }
  | { ok: false };

/** GB text-input value → bytes (blank = unlimited/null; bad input = invalid). */
export function parseGbToBytes(raw: string): ParsedQuota {
  const trimmed = raw.trim();
  if (!trimmed) return { ok: true, value: null };
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed) || parsed < 0) return { ok: false };
  return { ok: true, value: Math.round(parsed * GB) };
}

/** Non-negative integer count (blank = unlimited/null; bad input = invalid). */
export function parseCount(raw: string): ParsedQuota {
  const trimmed = raw.trim();
  if (!trimmed) return { ok: true, value: null };
  const parsed = Number(trimmed);
  if (!Number.isInteger(parsed) || parsed < 0) return { ok: false };
  return { ok: true, value: parsed };
}

export function bytesToGbInput(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined) return "";
  return String(Math.round((bytes / GB) * 100) / 100);
}
